import {
  useCreateOrUpdateLink,
  useGetEffectiveDateOptions,
  useGetLinkExpiration,
} from 'api/LinksAPI';
import { LinkExpirationSettingPayload } from 'modules/domains/links';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { ReactNode, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { ApiError } from 'services/api';
import { isString } from 'utils/helpers';
import { DEFAULT_DATE_OPTIONS } from '../constants';
import { ErrorResponse } from '../types';
import { DataQueryContext } from './DataQueryContext';

interface DataQueryContextProps {
  children: ReactNode;
  isOrgAdmin: boolean;
}

export const DataQueryContextProvider = ({ children, isOrgAdmin }: DataQueryContextProps) => {
  const dispatch = useDispatch();

  /** Fetch specific effective date options */
  const { data: effectiveDateOptionsResponse = DEFAULT_DATE_OPTIONS } = useGetEffectiveDateOptions({
    refetchOnWindowFocus: false,
    retry: false,
  });

  const effectiveDateOptions = useMemo(() => {
    // String values provided as `{{month}}-{{year}}` format, convert to more readable format
    return effectiveDateOptionsResponse.map((value) => ({
      id: value,
      label: value.replace('-', ' '),
      value,
    }));
  }, [effectiveDateOptionsResponse]);

  /** Fetch links from org to populate table */
  const { data, isLoading, refetch } = useGetLinkExpiration({
    enabled: isOrgAdmin,
    onError: (err) => {
      dispatch(
        addNegativeToastWithErrorId(
          'Failed to fetch link expiration settings',
          'LinkExpirationManagement/DataQueryContextProvider',
          err,
        ),
      );
    },
  });

  const { linkExpirationSettings = [] } = data || {};

  /** Update links */
  const { mutate } = useCreateOrUpdateLink();

  /** Utility function to manage updating one or more links */
  const handleUpdateLinks = (payload: LinkExpirationSettingPayload) => {
    mutate(payload, {
      onError: (errors: unknown) => {
        const { response } = errors as ApiError<ErrorResponse>;
        const responseMessage = response?.data?.message ?? '';
        dispatch(
          addNegativeToastWithErrorId(
            `Failed to save changes. ${responseMessage}`,
            'LinkExpirationManagement/Table',
            errors,
          ),
        );
      },
      onSuccess: (response) => {
        const message = isString(response) ? response : 'Changes saved successfully';
        dispatch(addPositiveToast(message));
        refetch();
      },
    });
  };

  return (
    <DataQueryContext.Provider
      value={{
        effectiveDateOptions,
        handleUpdateLinks,
        isLoading,
        links: linkExpirationSettings,
        refetch,
      }}
    >
      {children}
    </DataQueryContext.Provider>
  );
};
